import { useCallback, useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type z from 'zod';

/**
 * Tracks whether the current form values satisfy the provided Zod schema without
 * triggering React Hook Form validation state or displaying field errors.
 */
export function useIsFormValid<TFieldValues extends FieldValues>(
  form: Pick<UseFormReturn<TFieldValues>, 'getValues' | 'subscribe'>,
  schema: z.ZodType<TFieldValues>,
) {
  const getIsValid = useCallback(() => schema.safeParse(form.getValues()).success, [form, schema]);
  const [isValid, setIsValid] = useState(getIsValid);

  useEffect(() => {
    setIsValid(getIsValid());

    return form.subscribe({
      formState: { values: true },
      callback: () => {
        setIsValid((current) => {
          const next = getIsValid();
          return current === next ? current : next;
        });
      },
    });
  }, [form, getIsValid]);

  return isValid;
}
